/**
 * Announcements — Step 51.
 *
 * NOT an admin page. Kai: *"they should also be done from the create menu,
 * with a side menu, rather than being an admin thing."* The people who know
 * the mail relay is down are the ones working the desk; routing that through
 * an administrator is how it ends up said in forty replies instead of once.
 * Every post and edit is audited, which is the real control.
 *
 * Lists everything including the expired, which is the only place an old
 * announcement is useful ("what did we tell people last time?").
 *
 * The active window is two dates rather than an on/off switch, so an
 * announcement expires itself. The one people forget is always the stale one
 * still shouting about last Tuesday.
 */
import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Plus, Trash2 } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Sheet } from '../components/ui/sheet';
import { RichTextEditor } from '../components/ui/richtext';
import { apiFetch, apiSend } from '../lib/api';
import { qk } from '../lib/queryClient';
import { useDirty } from '../lib/dirty';
import { useUrlState } from '../lib/urlState';
import { fullTime } from '../lib/time';
import { ticketsApi } from '../features/tickets/api';
import { cn } from '../lib/utils';
import { useConfirm } from '../lib/confirm';

interface Row {
  id: string;
  title: string;
  bodyHtml: string | null;
  audience: string;
  visibleTo: string;
  groupId: string | null;
  severity: 'info' | 'warning' | 'critical';
  startsAt: number;
  endsAt: number | null;
  authorName: string | null;
  authorEmail: string | null;
}

const SEV_CLASS: Record<string, string> = {
  info: 'border-brand/40 text-brand',
  warning: 'border-amber-500/50 text-amber-700 dark:text-amber-400',
  critical: 'border-destructive/50 text-destructive',
};

const toLocal = (ms: number | null) => {
  if (!ms) return '';
  const d = new Date(ms - new Date(ms).getTimezoneOffset() * 60_000);
  return d.toISOString().slice(0, 16);
};

const BLANK = {
  id: '',
  title: '',
  bodyHtml: '',
  audience: 'org',
  groupId: '',
  severity: 'info',
  visibleTo: 'all',
  startsAt: '',
  endsAt: '',
};

export function AnnouncementsPage() {
  const confirm = useConfirm();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  // `?new=1` is how the Create menu lands here with the composer already
  // open, so "post an announcement" is one click from anywhere — and the
  // page itself still opens plainly, which is Kai's *"some way to open the
  // side bar without creating a new banner"*.
  const [wantNew, setWantNew] = useUrlState('new', '');
  const [form, setForm] = useState({ ...BLANK });
  /** What the form looked like when it opened — the only way to answer "has
   *  anything actually changed?" for an edit. */
  const [loaded, setLoaded] = useState({ ...BLANK });

  const list = useQuery({
    queryKey: qk.announcements.all,
    queryFn: () => apiFetch<{ entries: Row[] }>('/api/announcements'),
  });
  const groups = useQuery({ queryKey: qk.orgGroups, queryFn: ticketsApi.groups, staleTime: 5 * 60_000 });

  const dirty = open && (!!form.title.trim() || !!form.bodyHtml.trim());
  const clearDirty = useDirty(dirty);

  useEffect(() => {
    if (!wantNew) return;
    setForm({ ...BLANK, startsAt: toLocal(Date.now()) });
    setLoaded({ ...BLANK });
    setOpen(true);
    setWantNew('');
  }, [wantNew, setWantNew]);

  const refresh = () => {
    void qc.invalidateQueries({ queryKey: qk.announcements.all });
    void qc.invalidateQueries({ queryKey: qk.announcements.live });
  };

  const save = useMutation({
    mutationFn: () => {
      const body = {
        title: form.title.trim(),
        bodyHtml: form.bodyHtml || null,
        audience: form.audience,
        groupId: form.audience === 'group' ? form.groupId || null : null,
        severity: form.severity,
        visibleTo: form.visibleTo,
        startsAt: form.startsAt ? new Date(form.startsAt).getTime() : Date.now(),
        endsAt: form.endsAt ? new Date(form.endsAt).getTime() : null,
      };
      return form.id
        ? apiSend(`/api/announcements/${form.id}`, 'PATCH', body)
        : apiSend('/api/announcements', 'POST', body);
    },
    onSuccess: (r) => {
      if (!r.ok) return toast.error(r.error ?? 'Could not save.');
      clearDirty();
      setOpen(false);
      setForm({ ...BLANK });
      refresh();
    },
  });
  const destroy = useMutation({
    mutationFn: (id: string) => apiSend(`/api/announcements/${id}`, 'DELETE'),
    onSuccess: () => refresh(),
  });

  const entries = list.data?.entries ?? [];
  const now = Date.now();

  return (
    <div className="space-y-4 p-4">
      <div className="flex items-start gap-3">
        <div className="flex-1">
          <h1 className="text-lg font-semibold">Announcements</h1>
          <p className="text-sm text-muted-foreground">Said once, where everybody looks.</p>
        </div>
        <Button
          onClick={() => {
            setForm({ ...BLANK, startsAt: toLocal(Date.now()) });
            setLoaded({ ...BLANK });
            setOpen(true);
          }}
        >
          <Plus className="h-4 w-4" />
          New
        </Button>
      </div>

      {list.isError && (
        <Card>
          <CardContent className="space-y-2 p-4">
            <p className="text-sm font-medium text-destructive">Could not load announcements</p>
            <Button size="sm" variant="outline" onClick={() => void list.refetch()}>
              Retry
            </Button>
          </CardContent>
        </Card>
      )}
      {list.isPending && <Skeleton className="h-24 w-full" />}
      {list.data && entries.length === 0 && (
        <Card>
          <CardContent className="p-10 text-center text-sm text-muted-foreground">
            Nothing posted yet.
          </CardContent>
        </Card>
      )}

      {entries.length > 0 && (
        <Card>
          <CardContent className="p-0">
            {entries.map((a) => {
              const live = a.startsAt <= now && (a.endsAt === null || a.endsAt > now);
              return (
                <div key={a.id} className="flex items-center gap-3 border-b px-4 py-2.5 last:border-b-0">
                  <button
                    type="button"
                    onClick={() => {
                      setForm({
                        id: a.id,
                        title: a.title,
                        bodyHtml: a.bodyHtml ?? '',
                        audience: a.audience,
                        groupId: a.groupId ?? '',
                        severity: a.severity,
                        visibleTo: a.visibleTo ?? 'all',
                        startsAt: toLocal(a.startsAt),
                        endsAt: toLocal(a.endsAt),
                      });
                      setLoaded({
                        id: a.id,
                        title: a.title,
                        bodyHtml: a.bodyHtml ?? '',
                        audience: a.audience,
                        groupId: a.groupId ?? '',
                        severity: a.severity,
                        visibleTo: a.visibleTo ?? 'all',
                        startsAt: toLocal(a.startsAt),
                        endsAt: toLocal(a.endsAt),
                      });
                      setOpen(true);
                    }}
                    className="min-w-0 flex-1 rounded text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  >
                    <span className="block truncate text-sm font-medium">{a.title}</span>
                    <span className="block truncate text-xs text-muted-foreground">
                      {a.authorName ?? a.authorEmail} · {fullTime(a.startsAt)} →{' '}
                      {a.endsAt ? fullTime(a.endsAt) : 'no end'}
                    </span>
                  </button>
                  <Badge variant="outline" className={cn(SEV_CLASS[a.severity])}>
                    {a.severity}
                  </Badge>
                  <Badge variant="outline" className="text-muted-foreground">
                    {a.visibleTo === 'requesters'
                      ? 'End users'
                      : a.visibleTo === 'agents'
                        ? 'Agents'
                        : 'Everyone'}
                  </Badge>
                  {/* Live or not is the thing an admin scans for, so it is a
                      word rather than something to work out from two dates. */}
                  <Badge
                    variant="outline"
                    className={live ? 'border-emerald-500/40' : 'text-muted-foreground'}
                  >
                    {live ? 'Live' : a.startsAt > now ? 'Scheduled' : 'Expired'}
                  </Badge>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={async () => {
                      if (
                        await confirm({
                          title: `Delete “${a.title}”?`,
                          destructive: true,
                          confirmLabel: 'Delete',
                        })
                      )
                        destroy.mutate(a.id);
                    }}
                    aria-label={`Delete ${a.title}`}
                  >
                    <Trash2 className="h-4 w-4 text-destructive" />
                  </Button>
                </div>
              );
            })}
          </CardContent>
        </Card>
      )}

      <Sheet
        open={open}
        onClose={() => {
          clearDirty();
          setOpen(false);
        }}
        title={form.id ? 'Edit announcement' : 'New announcement'}
        wide
      >
        <form
          className="space-y-3"
          onSubmit={(e) => {
            e.preventDefault();
            if (form.title.trim()) save.mutate();
          }}
        >
          <div>
            <label className="mb-1 block text-xs font-medium text-muted-foreground">Title</label>
            <Input
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              autoFocus
            />
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-muted-foreground">Detail</label>
            <RichTextEditor value={form.bodyHtml} onChange={(html) => setForm({ ...form, bodyHtml: html })} />
          </div>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div>
              <label className="mb-1 block text-xs font-medium text-muted-foreground">Severity</label>
              <Select value={form.severity} onChange={(e) => setForm({ ...form, severity: e.target.value })}>
                <option value="info">Info</option>
                <option value="warning">Warning</option>
                <option value="critical">Critical</option>
              </Select>
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-muted-foreground">Who sees it</label>
              {/* WHICH AUDIENCE, as distinct from which group below. Kai:
                  announcements are "mainly for end users… but both should see
                  them", so `Everyone` is the default. */}
              <Select
                value={form.visibleTo}
                onChange={(e) => setForm({ ...form, visibleTo: e.target.value })}
              >
                <option value="all">Everyone</option>
                <option value="requesters">End users only</option>
                <option value="agents">Agents only</option>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div>
              <label className="mb-1 block text-xs font-medium text-muted-foreground">Scope</label>
              <Select value={form.audience} onChange={(e) => setForm({ ...form, audience: e.target.value })}>
                <option value="org">The whole organisation</option>
                <option value="group">One group</option>
              </Select>
              {form.audience === 'group' && form.visibleTo === 'requesters' && (
                <p className="mt-1 text-xs text-muted-foreground">
                  End users are in no groups, so nobody will see this.
                </p>
              )}
            </div>
          </div>
          {form.audience === 'group' && (
            <div>
              <label className="mb-1 block text-xs font-medium text-muted-foreground">Group</label>
              <Select value={form.groupId} onChange={(e) => setForm({ ...form, groupId: e.target.value })}>
                <option value="">Choose a group…</option>
                {(groups.data?.entries ?? []).map((g) => (
                  <option key={g.id} value={g.id}>
                    {g.name}
                  </option>
                ))}
              </Select>
            </div>
          )}
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div>
              <label className="mb-1 block text-xs font-medium text-muted-foreground">From</label>
              <Input
                type="datetime-local"
                value={form.startsAt}
                onChange={(e) => setForm({ ...form, startsAt: e.target.value })}
                className="dark:[color-scheme:dark]"
              />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-muted-foreground">Until</label>
              <Input
                type="datetime-local"
                value={form.endsAt}
                onChange={(e) => setForm({ ...form, endsAt: e.target.value })}
                className="dark:[color-scheme:dark]"
              />
              {!form.endsAt && (
                <p className="mt-1 text-xs text-muted-foreground">
                  Without an end date somebody has to remember to remove it.
                </p>
              )}
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                clearDirty();
                setOpen(false);
              }}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={
                !form.title.trim() ||
                save.isPending ||
                // Dirty as well as valid: an enabled Save on an untouched
                // edit form promises work it will not do
                // (`audit-ui[save-state]`).
                (!!form.id && JSON.stringify(form) === JSON.stringify(loaded))
              }
            >
              {save.isPending ? 'Saving…' : form.id ? 'Save' : 'Post'}
            </Button>
          </div>
        </form>
      </Sheet>
    </div>
  );
}
